import React, {useEffect, useState} from "react";
import SelectIcon from "../../assets/images/select_check_Blue.svg";

// --- Helper functions outside the component ---
function buildOpCodeMap(recommendationsByMileage: any) {
    const opCodeMap: Record<string, string[]> = {};
    recommendationsByMileage.forEach((childElement: any) => {
        childElement.recommendedOperations.forEach((recommendedOperation: any) => {
            if (!opCodeMap[recommendedOperation.operationCode]) {
                opCodeMap[recommendedOperation.operationCode] = [];
            }
            opCodeMap[recommendedOperation.operationCode].push(childElement.scheduleDesc);
        });
    });
    return opCodeMap;
}

function buildValuePackDataRow(recommendationsByMileage: any, columnMileageName: string) {
    const jsonValuePackData: any = {};
    jsonValuePackData[columnMileageName] = 'Value Package Price:';
    recommendationsByMileage.forEach((childElement: any) => {
        let columnScheduleName = childElement.scheduleDesc;
        jsonValuePackData[columnScheduleName] = childElement.valuePackagePrice;
    });
    return jsonValuePackData;
}

function buildFootnoteNumberMap(matchingFootnotes: any) {
    return matchingFootnotes.reduce((acc: any, footnote: any, idx: number) => {
        acc[footnote.operationCode] = idx + 1;
        return acc;
    }, {});
}

function buildRecommendationRows(
    recommendationsByMileage: any,
    opCodes: any,
    columnId: string,
    columnMileageName: string,
    opCodeMap: Record<string, string[]>,
    matchingFootnotes: any
) {
    const footnoteNumberMap = buildFootnoteNumberMap(matchingFootnotes);
    const dataRows: any[] = [];

    recommendationsByMileage.forEach((childElement: any) => {
        childElement.recommendedOperations.forEach((recommendedOperation: any) => {
            if (!opCodes.includes(recommendedOperation.operationCode)) {
                let jsonData: any = {};
                opCodes.push(recommendedOperation.operationCode);
                jsonData[columnId] = recommendedOperation.operationCode;

                const footnoteNumber = footnoteNumberMap[recommendedOperation.operationCode];
                jsonData[columnMileageName] = (
                    <>
                        {recommendedOperation.operationDescription} (
                        {recommendedOperation.operationCode}
                        {footnoteNumber && <sup>{footnoteNumber}</sup>}
                        ) - {recommendedOperation.operationPrice}
                    </>
                );
                let opCodeDataValues = opCodeMap[recommendedOperation.operationCode];
                opCodeDataValues.forEach((scheduleDesc: string) => {
                    jsonData[scheduleDesc] = 'Yes'
                });
                dataRows.push(jsonData);
            }
        });
    });
    return dataRows;
}

// Helper function to filter matching footnotes
function filterMatchingFootnotes(scheduleMaintenance: any) {
    if (!scheduleMaintenance?.recommendedFootnotes || !scheduleMaintenance?.recommendationsByMileage) return [];
    return scheduleMaintenance.recommendedFootnotes.filter((footnote: any) =>
        scheduleMaintenance.recommendationsByMileage.some((childElement: any) =>
            childElement.recommendedOperations.some((op: any) =>
                op.operationCode === footnote.operationCode
            )
        )
    );
}

function CFAdminMaintenanceScheduleForm ({scheduleMaintenance, vehicleDetails} : any) {

    const [columnValues, setColumnValues] = useState<any>([]);
    const [dataValues, setDataValues] = useState<any>([]);
    const [recommendedOperations, setRecommendedOperations] = useState<any>([]);
    const [valuePackagePrice, setValuePackagePrice] = useState<any>();
    const [recommendationsByMileageColumnValues, setRecommendationsByMileageColumnValues] = useState<any>([]);
    const [recommendationsByMileageDataValues, setRecommendationsByMileageDataValues] = useState<any>([]);
    const [recommendationsAtMileage, setRecommendationsAtMileage] = useState<any>([]);
    const [footnotes, setFootnotes] = useState<any>([]);

    function getVehicleConfigurations() {
        if (scheduleMaintenance) {

            createColumnsJson(scheduleMaintenance.attributes);
            createDataJson(scheduleMaintenance.attributes);
            if(scheduleMaintenance.iolmRecommendations){
                setRecommendedOperations(scheduleMaintenance.iolmRecommendations?.recommendedOperations);
                setValuePackagePrice(scheduleMaintenance.iolmRecommendations?.valuePackagePrice)
            }

            // Use helper for matchingFootnotes
            const matchingFootnotes = filterMatchingFootnotes(scheduleMaintenance);
            setFootnotes(matchingFootnotes);

            if(scheduleMaintenance.recommendationsByMileage){
                createRecommendationsByMileageColumnJson(scheduleMaintenance.recommendationsByMileage);
                createRecommendationsByMileageDataJson(scheduleMaintenance.recommendationsByMileage, matchingFootnotes);
            }
            if(scheduleMaintenance.recommendationsAtMileage){
                setRecommendationsAtMileage(scheduleMaintenance.recommendationsAtMileage);
            }
        }
    }

    function createRecommendationsByMileageColumnJson(recommendationsByMileage: any)
    {
        let jsonData : any = [];
        jsonData.push('Mileage:');
        recommendationsByMileage.forEach((childElement: any) => {
            jsonData.push(childElement.scheduleDesc);
        });

        setRecommendationsByMileageColumnValues(jsonData);
        return jsonData;
    }

    function createRecommendationsByMileageDataJson(recommendationsByMileage: any, matchingFootnotes: any)
    {
        let opCodes : any = [];
        let dataValues : any = [];
        let columnId = 'OpCode';
        let columnMileageName = 'Mileage:';
        const opCodeMap = buildOpCodeMap(recommendationsByMileage);
        dataValues.push(buildValuePackDataRow(recommendationsByMileage, columnMileageName));
        dataValues.push(
            ...buildRecommendationRows(
                recommendationsByMileage,
                opCodes,
                columnId,
                columnMileageName,
                opCodeMap,
                matchingFootnotes
            )
        );
        setRecommendationsByMileageDataValues(dataValues);
        return dataValues;
    }


    function createColumnsJson(attributes: any)
    {
        let jsonData : any = [];
        attributes.forEach((childElement: any) => {
            jsonData.push(childElement.attributeDescription);
        });

        setColumnValues(jsonData);
        return jsonData;
    }

    function createDataJson(attributes: any)
    {
        let dataValues : any = [];
        let jsonData : any = {};
        attributes.forEach((childElement: any) => {
            let columnName = childElement.attributeDescription;
            jsonData[columnName] = childElement.attributeValueDescription;
        });

        dataValues.push(jsonData);
        setDataValues(dataValues);
        return jsonData;
    }


    useEffect(() => {
        getVehicleConfigurations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scheduleMaintenance]);

    const renderVehicleTable = (columnValues : any , dataValues: any) => {
        return (
            <table className="inner-table" style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead>
                <tr>
                    {columnValues.map((heading: any, idx: number) => (
                        <th
                            style={{
                                background: 'aliceblue',
                                color: 'black',
                                padding: '8px',
                                border: '1px solid #ddd',
                                whiteSpace: 'nowrap',
                                position: idx === 0 ? 'sticky' : undefined,
                                left: idx === 0 ? 0 : undefined,
                                zIndex: idx === 0 ? 2 : undefined
                            }}
                            key={heading}
                        >
                            {heading}
                        </th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {dataValues.map((row: any, rowIndex: any) => (
                    <tr key={row.operationCode ? row.operationCode + '-' + rowIndex : rowIndex}>
                        {columnValues.map((heading: any, idx: number) => (
                            <td
                                style={{
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    whiteSpace: 'nowrap',
                                    position: idx === 0 ? 'sticky' : undefined,
                                    left: idx === 0 ? 0 : undefined,
                                    zIndex: idx === 0 ? 1 : undefined,
                                    background: idx === 0 ? 'white' : undefined
                                }}
                                key={rowIndex + '-' + heading}
                            >
                                {row[heading] === 'Yes' ? <img src={SelectIcon} alt="Xcelvations Logo"/> : row[heading]}
                            </td>
                        ))}
                    </tr>
                ))}
                </tbody>
            </table>
        );
    };

    const renderRecommendationsAtMileage = () => {
        return (
            <table className="inner-table" style={{width: '100%', borderCollapse: 'collapse'}}>
                <tbody>
                {recommendationsAtMileage.recommendedOperations.map((row: any, rowIndex: any) => (
                    <tr key={row.operationCode ? row.operationCode + '-' + rowIndex : rowIndex}>
                        <td style={{padding: '8px', border: '1px solid #ddd'}}><img src={SelectIcon} alt="Xcelvations Logo"/></td>
                        <td style={{padding: '8px', border: '1px solid #ddd'}}>{row.operationCode}</td>
                        <td style={{padding: '8px', border: '1px solid #ddd'}}>{row.operationDescription}</td>
                        <td style={{padding: '8px', border: '1px solid #ddd'}}>{row.operationPrice}</td>
                    </tr>
                ))}
                <tr>
                    <td colSpan={3} style={{padding: '8px', border: '1px solid #ddd'}}>Total:</td>
                    <td style={{padding: '8px', border: '1px solid #ddd'}}>{recommendationsAtMileage.totalPrice}</td>
                </tr>
                <tr>
                    <td colSpan={3} style={{padding: '8px', border: '1px solid #ddd'}}>Value Package Price:</td>
                    <td style={{padding: '8px', border: '1px solid #ddd'}}>{recommendationsAtMileage.valuePackagePrice}</td>
                </tr>
                </tbody>
            </table>
        );
    };

    const renderRecommendedOperations = () => {
        return (
            <>
            {
                    recommendedOperations && recommendedOperations.length > 0 &&
                    <div>
                        <ul>
                            {recommendedOperations.map((recommendedOperation: any, index: number) => (
                                <li key={recommendedOperation.operationCode ? recommendedOperation.operationCode + '-' + index : index}>
                                    {recommendedOperation.operationDescription} ({recommendedOperation.operationCode}) - {recommendedOperation.operationPrice}
                                </li>
                            ))}
                        </ul>
                        <div className="main-home-ext2c">
                            <b>Oil Change Value Package Price: </b>{valuePackagePrice}
                        </div>
                    </div>

                }
            </>
        );
    };

    return (
            <div className="main-home-container">
                <div className="main-home-content">
                    <div className="main-home">
                        <div>
                            <div className="main-home-ext1">
                                <b>Scheduled Maintenance: {scheduleMaintenance.mmyDescription}</b>
                                <div
                                    className="main-home-table-sub"> {vehicleDetails.vin === '' ? '' : "VIN: " + vehicleDetails.vin} </div>
                            </div>
                            <div className="main-home-ext2">
                                <b>Driving Condition:</b> {scheduleMaintenance.drivingConditionDesc}
                            </div>
                            <div className="main-home-table">
                                <div style={{
                                    maxWidth: '100%',
                                    overflowX: 'auto',
                                    overflowY: 'auto',
                                    maxHeight: '300px'
                                }}>
                                    {renderVehicleTable(columnValues, dataValues)}
                                </div>
                            </div>
                            {vehicleDetails && vehicleDetails.scheduleCode === 'FS' &&
                                <div className="main-home-ext1">
                                    <b> Ford Recommended Maintenance </b>
                                </div>
                            }
                            {recommendationsAtMileage?.recommendedOperations && recommendationsAtMileage.recommendedOperations.length > 1 &&
                                <div className="main-home-ext1">
                                    <b> Ford Recommended Maintenance - {recommendationsAtMileage.scheduleDesc} </b>
                                </div>
                            }
                            {vehicleDetails && vehicleDetails.scheduleCode === 'FS' && recommendedOperations && recommendedOperations.length > 0 &&
                                <>
                                    <div className="main-home-ext1">
                                        <b> Ford Recommended Maintenance as indicated by Intelligent Oil Life Monitor
                                            System</b>
                                    </div>
                                    <div className="main-home-ext2">
                                        <b>This vehicle is equipped with Intelligent Oil Life Monitor (IOLM).
                                            Based on driving conditions, the IOLM will display a message when your
                                            vehicle needs
                                            an oil
                                            change.
                                            Every time the display reads 'Oil Change Required', the following
                                            maintenance is
                                            recommended:</b>
                                    </div>
                                    <div className="main-home-table">
                                        {renderRecommendedOperations()}
                                    </div>
                                </>
                            }
                            {vehicleDetails && vehicleDetails.scheduleCode !== 'FS' && recommendedOperations && recommendedOperations.length > 0 &&
                                <>
                                    <div className="main-home-ext1">
                                        <b> Ford Recommended Maintenance - Every IOLM Oil Change</b>
                                    </div>
                                    <div className="main-home-ext1">
                                        <b>Is it time for an Oil Change? Check percentage oil life remaining in the
                                            Message Center.</b>
                                    </div>
                                    <div className="main-home-ext2">
                                        This vehicle is equipped with Intelligent Oil Life Monitor (IOLM).
                                        Based on driving conditions, the IOLM will display a message when your vehicle
                                        needs an oil change.
                                        If the display is showing <b>Oil Change Required</b>, It is time!

                                    </div>
                                    <div className="main-home-table">
                                        {renderRecommendedOperations()}
                                    </div>
                                </>
                            }
                            {recommendationsAtMileage?.recommendedOperations && recommendationsAtMileage.recommendedOperations.length > 0 &&
                                <>
                                    <div className="main-home-ext2">
                                        <b>Ford Recommended Operations</b>
                                    </div>
                                    <div className="main-home-table">
                                        <div style={{
                                            maxWidth: '100%',
                                            overflowX: 'auto',
                                            overflowY: 'auto',
                                            maxHeight: '300px'
                                        }}>
                                            {renderRecommendationsAtMileage()}
                                        </div>
                                    </div>
                                </>
                            }
                            <div style={{
                                maxWidth: '100%',
                                overflowX: 'auto',
                                overflowY: 'auto',
                                maxHeight: '400px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                margin: '10px 0'
                            }}>
                                {renderVehicleTable(recommendationsByMileageColumnValues, recommendationsByMileageDataValues)}
                            </div>
                            {footnotes.length > 0 && (
                                <div className="main-home-ext2n" style={{ marginTop: 8 }}>
                                    <b>Footnotes:</b>
                                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                                        {footnotes.map((footnote: any, idx: number) => (
                                            <li key={footnote.operationCode ? footnote.operationCode + '-' + idx : idx} style={{ listStyle: 'none' }}>
                                                <sup>{idx + 1}</sup> {footnote.operationDescription}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <div className="main-home-ext2n">
                                {scheduleMaintenance.notes}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
    )
}

export default CFAdminMaintenanceScheduleForm;
